import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { SwipeableDrawer, Button, Modal, Box, Typography, IconButton } from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import Billing from './Billing';
import theme from '../../theme';
import { ThemeProvider } from '@mui/material/styles';
import Toast from '../../components/utils/Toast';
import { useTranslation } from 'react-i18next';
import { track } from '../../third-party/MixPanel';

const Credits: React.FC = () => {
    const { user, refreshUserData, isDevVersion, error } = useAuth();
    const { t } = useTranslation();
    const [openStripe, setOpenStripe] = useState(false);
    const [localCredits, setLocalCredits] = useState(user?.credits || 0);
    const [showToast, setShowToast] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);

    useEffect(() => {
        if (user?.credits !== undefined) {
            setLocalCredits(user.credits);
        }
    }, [user?.credits]);

    // Track when credits page is opened
    useEffect(() => {
        track('credits_page_opened', {
            currentCredits: user?.credits || 0,
            hasCredits: (user?.credits || 0) > 0
        });
    }, []);

    // Show error modal in dev version when there's an error
    useEffect(() => {
        if (isDevVersion && error) {
            setShowErrorModal(true);
        }
    }, [isDevVersion, error]);

    const handleOpenBilling = () => {
        console.log('🟢 [Credits] handleOpenBilling called');
        track('billing_modal_opened', {
            currentCredits: user?.credits || 0,
            source: 'credits_page'
        });
        console.log('🟢 [Credits] Setting openStripe to true');
        setOpenStripe(true);
        console.log('🟢 [Credits] openStripe state updated');
    };

    const handleSuccess = async () => {
        setOpenStripe(false);

        setTimeout(async () => {
            await refreshUserData();
            console.log("successful billing")
            setShowToast(true);
        }, 1000);
        await refreshUserData();
    };

    if (!user) {
        return (
            <div className="flex items-center justify-center p-4">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-purple-500"></div>
                <span className="ml-2 text-gray-600">{t('credits.loading')}</span>
            </div>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <div className="flex flex-col items-center justify-center p-4 space-y-4 mt-4">
                <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-md">
                    <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center justify-between w-full">
                            <span className="text-lg font-medium text-gray-700">{t('credits.availableCredits')}</span>
                            <div className="flex items-center gap-4">
                                <span className="text-2xl font-bold text-purple-600">{localCredits}</span>
                                <Button
                                    onClick={() => {
                                        console.log('🔵 [Credits] Buy Credits button clicked');
                                        handleOpenBilling();
                                    }}
                                    sx={{
                                        display: isDevVersion ? 'block' : 'none',
                                        color: '#FFFFFF',
                                        py: 1.5,
                                        fontSize: '0.8rem',
                                        fontFamily: '"Playfair Display", serif',
                                        background: 'linear-gradient(135deg, #2B2E4A 0%, #3D4066 100%)',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #3D4066 0%, #2B2E4A 100%)',
                                        }
                                    }}
                                >
                                    {t('credits.buyCredits')}
                                </Button>
                            </div>
                        </div>
                    </div>

                    <div className="border-t pt-4">
                        <p className="text-sm text-gray-600 mb-0">
                            {t('credits.creditsDescription')}
                        </p>
                    </div>
                </div>
                <SwipeableDrawer
                    anchor="bottom"
                    open={openStripe}
                    onClose={() => {
                        console.log('🟠 [Credits] SwipeableDrawer onClose called');
                        setOpenStripe(false);
                    }}
                    onOpen={() => {
                        console.log('🟠 [Credits] SwipeableDrawer onOpen called');
                        setOpenStripe(true);
                    }}
                    slotProps={{
                        paper: {
                            sx: {
                                height: '90%',
                                borderTopLeftRadius: '16px',
                                borderTopRightRadius: '16px',
                                padding: '20px',
                                overflowY: 'auto',
                            }
                        }
                    }}
                >
                    {openStripe && (() => {
                        console.log('🟡 [Credits] Rendering Billing component inside drawer');
                        return <Billing onSuccess={handleSuccess} padding={0} />;
                    })()}
                </SwipeableDrawer>
                <div className="bg-white rounded-lg shadow-md p-6 w-full max-w-md mt-4 hidden">
                    <h3 className="text-lg font-medium text-gray-700 mb-3">{t('credits.recentActivity')}</h3>
                    {user?.creditHistory?.slice(0, 5)?.map((history: any, index: number) => (
                        <div key={index} className="flex justify-between items-center py-2 border-b last:border-0">
                            <span className="text-gray-600">{history.action}</span>
                            <span className={`font-medium ${history.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                                {history.type === 'credit' ? '+' : '-'}{history.amount}
                            </span>
                        </div>
                    )) || (
                            <p className="text-sm text-gray-500">{t('credits.noRecentActivity')}</p>
                        )}
                </div>
                <Toast
                    open={showToast}
                    message={t('credits.creditsUpdatedSuccess')}
                    severity="success"
                    onClose={() => setShowToast(false)}
                />

                {/* Dev Error Modal */}
                <Modal
                    open={showErrorModal && isDevVersion}
                    onClose={() => setShowErrorModal(false)}
                    aria-labelledby="error-modal-title"
                    aria-describedby="error-modal-description"
                >
                    <Box sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: { xs: '90%', sm: 500 },
                        bgcolor: 'background.paper',
                        borderRadius: 2,
                        boxShadow: 24,
                        p: 4,
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography id="error-modal-title" variant="h6" component="h2" sx={{ color: '#d32f2f', fontWeight: 'bold' }}>
                                🚨 Development Error
                            </Typography>
                            <IconButton onClick={() => setShowErrorModal(false)} size="small">
                                <CloseIcon />
                            </IconButton>
                        </Box>
                        <Typography id="error-modal-description" sx={{ mt: 2, mb: 2 }}>
                            <strong>Error Message:</strong>
                        </Typography>
                        <Box sx={{
                            bgcolor: '#fff3e0',
                            p: 2,
                            borderRadius: 1,
                            border: '1px solid #ffb74d',
                            mb: 2
                        }}>
                            <Typography sx={{ fontFamily: 'monospace', fontSize: '0.9rem', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {error || 'No error message available'}
                            </Typography>
                        </Box>
                        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                            This modal is only visible in development mode (test@test.com)
                        </Typography>
                    </Box>
                </Modal>
            </div>
        </ThemeProvider >
    );
};

export default Credits;
